/* eslint-disable @typescript-eslint/naming-convention */

import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild, ViewChildren, ChangeDetectionStrategy } from '@angular/core';

import { ItineraryStep } from '../models/itinerary';
import { DeveloperService } from '../services/developer.service';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { DocumentConfigItineraryStep, ItineraryStepDocumentConfigEntity } from '../models/itinerary-step-document-config-entity';
import { GenericModalService, ModalSettingsBase, ModalSettingsTemplate } from '../../shared/generic-modal/generic-modal.service';
import { DataFieldGroup, DataFieldValue } from '../../shared/datafields/models';
import { UntypedFormGroup, FormsModule } from '@angular/forms';
import { Guid } from '../../shared/utils';
import { DataFieldGroupContainerComponent } from '../../shared/datafields/datafield-group-container.component';
import { Distribution } from 'src/app/distribution/models';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { DxListModule, DxDataGridModule } from 'devextreme-angular';
import { DxoItemDraggingModule, DxiColumnModule } from 'devextreme-angular/ui/nested';
import { DxTemplateModule } from 'devextreme-angular/core';

import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';

@Component({
    selector: 'app-developer-document-configs-itinerary',
    templateUrl: './developer-document-configs-itinerary.component.html',
    styleUrls: ['./developer-document-configs-itinerary.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, DxListModule, DxoItemDraggingModule, DxTemplateModule, FormsModule, DataFieldGroupContainerComponent, DxDataGridModule, BizDataGridDirective, DxiColumnModule]
})
export class DeveloperDocumentConfigItineraryComponent implements OnInit, OnChanges {
    availableSteps: ItineraryStep[] = [];
    chosenSteps: ItineraryStepConfig[] = [];
    showItineraryStepShop = false;
    selectedConfigSteps: ItineraryStepConfig[] = [];
    distributionsEffectedByItinerarySteps: Distribution[] = [];
    distributionsBlockingChange: Distribution[] = [];

    @ViewChildren('groupContainer') groupContainers: DataFieldGroupContainerComponent[];
    @ViewChild('viewDistributionsEffectedByItineraryStepsTemplate', { static: true }) private distributionsEffectedByItineraryStepsTemplate: TemplateRef<any>;
    @ViewChild('viewDistributionsBlockingChangeTemplate', { static: true }) private distributionsBlockingChangeTemplate: TemplateRef<any>;

    @Output() cancelled = new EventEmitter<void>();
    @Input() documentConfigId: number;

    constructor(
        private developerService: DeveloperService,
        private loadingPanelService: LoadingPanelService,
        private modalService: GenericModalService,
        private readonly changeDetectorRef: ChangeDetectorRef) {

        this.onReorder = this.onReorder.bind(this);
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes.documentConfigId) {
            if (this.documentConfigId) {
                await this.refresh();
            }
            else {
                this.chosenSteps = [];
            }
        }
    }

    async ngOnInit() {
        if (this.availableSteps.length === 0) {
            this.availableSteps = await this.developerService.getItinerarySteps();
        }
    }

    get isValid() {
        return this.chosenSteps.every(s => s.formGroup.valid);
    }

    async refresh(): Promise<void> {
        this.loadingPanelService.showLoadingPanelDirect('Loading configuration...');

        if (this.availableSteps.length === 0) {
            this.availableSteps = await this.developerService.getItinerarySteps();
        }


        const stepConfigs = await this.developerService.getDocumentConfigItinerarySteps(this.documentConfigId);
        this.chosenSteps = stepConfigs.map(sc => ({
            configStep: sc,
            step: this.availableSteps.find(is => is.Id === sc.ItineraryStepId),
            formGroup: new UntypedFormGroup({}),
            uniqueId: Guid.newGuid()
        } as ItineraryStepConfig));

        this.chosenSteps = this.chosenSteps.sort((a, b) => a.configStep.SortOrder - b.configStep.SortOrder);

        const promises = [];
        this.chosenSteps.forEach(s => { // Load all configurations for the chosen steps
            promises.push(this.developerService.getDocumentConfigItineraryStepDataFieldGroups(s.step.Id, s.configStep.Id).then(r => s.groups = r));
        });
        await Promise.all(promises);

        if (this.chosenSteps.length > 0) {
            this.selectedConfigSteps = [this.chosenSteps[0]];
        }
        else {
            this.showItineraryStepShop = true;
        }

        this.loadingPanelService.hideLoadingPanel();
        this.changeDetectorRef.detectChanges(); // Necessary for form-validation not giving "expression has changed" error
    }

    async addStep(step: ItineraryStep) {
        const stepConfig = {
            step,
            configStep: {
                Description: step.Description,
                ItineraryStepId: step.Id,
                SortOrder: this.chosenSteps.length + 1,
                DocumentConfigId: this.documentConfigId,
                StepConfiguration: null,
                ConfigureOnDistribution: false,
                OptionalOnDistribution: false,
                DefaultActiveOnDistribution: true,
            },
            groups: await this.developerService.getDocumentConfigItineraryStepDataFieldGroups(step.Id),
            formGroup: new UntypedFormGroup({}),
            uniqueId: Guid.newGuid()
        } as ItineraryStepConfig;

        this.chosenSteps.push(stepConfig);
        this.selectedConfigSteps = [stepConfig];
        this.showItineraryStepShop = false;

        this.changeDetectorRef.detectChanges(); // Necessary for form-validation not giving "expression has changed" error
    }

    removeStep(step: ItineraryStepConfig) {
        this.modalService.confirmDirect('Remove step', `Remove step: ${step.step.Name}?`).then(() => {
            this.chosenSteps.splice(this.chosenSteps.indexOf(step), 1);
        }).catch(() => { });
    }

    stepConfigSelected(e: any) {
        this.selectedConfigSteps = [e.itemData];
    }

    onReorder(e) {
        const data = this.chosenSteps.splice(e.fromIndex, 1);
        this.chosenSteps.splice(e.toIndex, 0, data[0]);
    }

    onConfigureOnDistributionChange(step: ItineraryStepConfig): void {
        if (!step.configStep.ConfigureOnDistribution) {
            step.configStep.OptionalOnDistribution = false;
            step.configStep.DefaultActiveOnDistribution = true;
        }
    }

    onOptionalOnDistributionChange(step: ItineraryStepConfig): void {
        if (!step.configStep.OptionalOnDistribution) {
            step.configStep.DefaultActiveOnDistribution = true;
        }
    }

    async showDistributionsEffectedByItinerarySteps(){
        this.loadingPanelService.showLoadingPanelDirect('Loading...');
        const stepsToSave = this.getStepsToSave();
        this.distributionsEffectedByItinerarySteps = await this.developerService.getDistributionsEffectedByItinerarySteps(stepsToSave);
        this.loadingPanelService.hideLoadingPanel();

        if(this.distributionsEffectedByItinerarySteps.length <= 0){
            await this.save(stepsToSave);
        }
        else{
            this.distributionsEffectedByItinerarySteps.forEach(d =>{
                d['ConcatenatedFormatVariantVersionFrom'] = `${d.DocumentConfigIn.Format.FormatName}/${d.DocumentConfigIn.Variant.VariantName}/${d.DocumentConfigIn.VariantVersion.VariantVersionName}`;
                d['ConcatenatedFormatVariantVersionTo'] = `${d.DocumentConfigOut.Format.FormatName}/${d.DocumentConfigOut.Variant.VariantName}/${d.DocumentConfigOut.VariantVersion.VariantVersionName}`;
            });

            const settings = {} as ModalSettingsBase;
            settings.size = 'lg';
            settings.backdrop = 'static';
            settings.windowClass = 'hugeModal';

            const templateSettings = new ModalSettingsTemplate();
            Object.assign(templateSettings, settings);

            try {
                await this.modalService.openTemplate(this.distributionsEffectedByItineraryStepsTemplate, templateSettings);
                await this.save(stepsToSave);
            }
            catch (error) {
                console.log(error);
            }
        }
    }

    async save(stepsToSave: DocumentConfigItineraryStep[]) {
        this.loadingPanelService.showLoadingPanelDirect('Saving itinerary configuration...');
        this.distributionsBlockingChange = [];
        this.distributionsBlockingChange = await this.developerService.getDistributionsBlockingChange(stepsToSave);
        if (this.distributionsBlockingChange.length > 0) {
            this.distributionsBlockingChange.forEach(d =>{
                d['ConcatenatedFormatVariantVersionFrom'] = `${d.DocumentConfigIn.Format.FormatName}/${d.DocumentConfigIn.Variant.VariantName}/${d.DocumentConfigIn.VariantVersion.VariantVersionName}`;
                d['ConcatenatedFormatVariantVersionTo'] = `${d.DocumentConfigOut.Format.FormatName}/${d.DocumentConfigOut.Variant.VariantName}/${d.DocumentConfigOut.VariantVersion.VariantVersionName}`;
            });

            const settings = {} as ModalSettingsBase;
            settings.size = 'lg';
            settings.backdrop = 'static';
            settings.windowClass = 'hugeModal';

            const templateSettings = new ModalSettingsTemplate();
            Object.assign(templateSettings, settings);

            try {
                await this.modalService.openTemplate(this.distributionsBlockingChangeTemplate, templateSettings);
            }
            catch (error) {
                console.log(error);
            }
        }
        else {
            try {
                await this.developerService.saveDocumentConfigItineraryStepDataFieldGroups(stepsToSave, this.documentConfigId);
            } finally{
                this.loadingPanelService.hideLoadingPanel();
            }
            this.cancel();
        }
        this.loadingPanelService.hideLoadingPanel();
    }

    private getStepsToSave(){
        const stepsToSave = this.chosenSteps.map((s, index) => {
            s.configStep.SortOrder = index; // Set sort-order...
            return {
                DataFieldValues: DataFieldGroup.getDataFieldValues(s.groups, true),
                ItineraryStepDocumentConfigEntity: s.configStep
            } as DocumentConfigItineraryStep;
        });
        return stepsToSave;
    }

    cancel() {
        if (this.cancelled) {
            this.cancelled.emit();
        }
    }
}

export interface ItineraryStepConfig {
    configStep: ItineraryStepDocumentConfigEntity;
    step: ItineraryStep;
    groups: DataFieldGroup[];
    formGroup: UntypedFormGroup;
    uniqueId: string;
}
